import React, { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Card,
  CardBody,
  Progress,
  Row,
  Col,
  Input,
  Button,
  Label,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CompanyDocumentType,
  PartnerDocumentType,
  IndividualDocumentType,
  DocumentOwnerType,
  getDocumentTypeLabel,
  UploadDocumentProgress,
  getDocumentSphereLabel,
  getDocumentSphereColor,
  formatValidityDays,
} from '../../../../types/documents';
import { documentsService } from '../../../../services/documentsService';
import { documentUploadSignalR } from '../../../../services/documentUploadSignalR';
import { CompanyDocumentMetadata } from '../../../../utils/documentMetadata';

interface DocumentUploadZoneProps {
  ownerType: DocumentOwnerType;
  ownerId: string; // companyId, partnerId, or individualId
  companyId: string; // Always needed for partner docs
  onUploadComplete: () => void;
  onUpload: (
    file: File,
    documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType,
    onProgress: (progress: number) => void
  ) => Promise<void>;
}

interface PendingFile {
  file: File;
  id: string;
  documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType;
  customFileName: string;
  startDate?: string; // ISO date string
  expirationDate?: string; // ISO date string
}

export interface DocumentUploadZoneRef {
  validatePendingFiles: () => boolean;
  hasPendingFiles: () => boolean;
  uploadAllFiles: () => Promise<boolean>;
}

const DocumentUploadZone = forwardRef<DocumentUploadZoneRef, DocumentUploadZoneProps>(({
  ownerType,
  ownerId,
  companyId,
  onUploadComplete,
  onUpload,
}, ref) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadQueue, setUploadQueue] = useState<UploadDocumentProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Connect to SignalR hub when component mounts
  useEffect(() => {
    const initSignalR = async () => {
      try {
        await documentUploadSignalR.startConnection();
      } catch (error) {
        console.error('Failed to start SignalR connection:', error);
      }
    };

    initSignalR();

    // Cleanup on unmount
    return () => {
      documentUploadSignalR.stopConnection();
    };
  }, []);

  // Expose validation methods to parent components
  useImperativeHandle(ref, () => ({
    validatePendingFiles: () => {
      // Check if there are any unclassified files
      const unclassifiedFiles = pendingFiles.filter(
        (pf) => pf.documentType === getDefaultDocumentType()
      );

      if (unclassifiedFiles.length > 0) {
        // Mark unclassified files with validation errors
        const errorIds = new Set(unclassifiedFiles.map((pf) => pf.id));
        setValidationErrors(errorIds);

        toast.error(
          t('Por favor, classifique todos os documentos antes de continuar')
        );
        return false;
      }

      setValidationErrors(new Set());
      return true;
    },
    hasPendingFiles: () => {
      return pendingFiles.length > 0;
    },
    uploadAllFiles: async () => {
      await handleUploadAll();
      return true;
    },
  }));

  const acceptedFileTypes = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.webp',
    '.zip',
    '.rar',
    '.7z',
    '.p12',
    '.pfx',
    '.cer',
    '.crt',
    '.txt',
    '.csv',
  ].join(',');

  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const getDefaultDocumentType = () => {
    // Return -1 to indicate no selection (empty option)
    return -1;
  };

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return t('Arquivo muito grande. Tamanho máximo: 5MB');
    }

    // Check file extension
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!acceptedFileTypes.includes(extension)) {
      return t('Tipo de arquivo não permitido');
    }

    return null;
  }, [t, maxFileSize, acceptedFileTypes]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      const validFiles: File[] = [];

      // Validate all files first
      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          toast.error(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) return;

      // Add files to pending queue (not uploaded yet)
      const newPendingFiles: PendingFile[] = validFiles.map((file) => ({
        file,
        id: `${Date.now()}-${Math.random()}`,
        documentType: getDefaultDocumentType(),
        customFileName: file.name,
      }));

      setPendingFiles((prev) => [...prev, ...newPendingFiles]);
      toast.info(t('Classifique cada documento antes de enviar'));
    },
    [validateFile, t]
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleBrowseClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleDocumentTypeChange = (fileId: string, documentType: number) => {
    setPendingFiles((prev) =>
      prev.map((pf) => {
        if (pf.id !== fileId) return pf;

        // Check if this document type requires dates (only for Company documents)
        if (ownerType === DocumentOwnerType.Company && documentType !== getDefaultDocumentType()) {
          const hasExpiration = CompanyDocumentMetadata.hasExpiration(documentType as CompanyDocumentType);

          if (hasExpiration) {
            // Set default start date to today
            const today = new Date();
            const startDate = today.toISOString().split('T')[0];

            // Auto-calculate expiration date
            const expirationDateObj = CompanyDocumentMetadata.calculateExpirationDate(
              documentType as CompanyDocumentType,
              today
            );
            const expirationDate = expirationDateObj?.toISOString().split('T')[0];

            return {
              ...pf,
              documentType,
              startDate,
              expirationDate,
            };
          }
        }

        return { ...pf, documentType };
      })
    );

    // Clear validation error for this file when user selects a type
    if (documentType !== getDefaultDocumentType()) {
      setValidationErrors((prev) => {
        const newErrors = new Set(prev);
        newErrors.delete(fileId);
        return newErrors;
      });
    }
  };

  const handleStartDateChange = (fileId: string, startDate: string) => {
    setPendingFiles((prev) =>
      prev.map((pf) => {
        if (pf.id !== fileId) return pf;

        // Auto-calculate expiration date based on document type
        if (ownerType === DocumentOwnerType.Company && pf.documentType !== getDefaultDocumentType()) {
          const startDateObj = new Date(startDate);
          const expirationDateObj = CompanyDocumentMetadata.calculateExpirationDate(
            pf.documentType as CompanyDocumentType,
            startDateObj
          );
          const expirationDate = expirationDateObj?.toISOString().split('T')[0];

          return {
            ...pf,
            startDate,
            expirationDate,
          };
        }

        return { ...pf, startDate };
      })
    );
  };

  const handleExpirationDateChange = (fileId: string, expirationDate: string) => {
    setPendingFiles((prev) =>
      prev.map((pf) =>
        pf.id === fileId ? { ...pf, expirationDate } : pf
      )
    );
  };

  const handleRemovePendingFile = (fileId: string) => {
    setPendingFiles((prev) => prev.filter((pf) => pf.id !== fileId));

    // Clear validation error for this file
    setValidationErrors((prev) => {
      const newErrors = new Set(prev);
      newErrors.delete(fileId);
      return newErrors;
    });
  };

  const handleUploadAll = async () => {
    if (pendingFiles.length === 0) return;

    // Validate all files are classified
    const unclassifiedFiles = pendingFiles.filter(
      (pf) => pf.documentType === getDefaultDocumentType()
    );

    if (unclassifiedFiles.length > 0) {
      // Mark unclassified files with validation errors
      const errorIds = new Set(unclassifiedFiles.map((pf) => pf.id));
      setValidationErrors(errorIds);

      toast.error(
        t('Por favor, classifique todos os documentos antes de enviar')
      );
      return;
    }

    // Clear any previous validation errors
    setValidationErrors(new Set());
    setIsUploading(true);

    // Generate unique upload session ID
    const uploadSessionId = `upload-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Create upload queue entries
    const newUploads: UploadDocumentProgress[] = pendingFiles.map((pf) => ({
      file: pf.file,
      customFileName: pf.customFileName,
      documentType: pf.documentType,
      progress: 0,
      status: 'pending' as const,
    }));

    setUploadQueue(newUploads);

    // Store pending files for batch upload
    const filesToUpload = [...pendingFiles];
    setPendingFiles([]);

    try {
      // Ensure SignalR is connected
      if (!documentUploadSignalR.isConnected()) {
        await documentUploadSignalR.startConnection();
      }

      // Join upload session to receive progress updates
      await documentUploadSignalR.joinUploadSession(uploadSessionId);

      // Subscribe to SignalR events
      const unsubscribeProgress = documentUploadSignalR.onUploadProgress((data) => {
        if (data.uploadSessionId === uploadSessionId) {
          // Update overall progress based on current/total
          // const overallProgress = Math.round((data.current / data.total) * 100); // Reserved for future use

          // Update all files that are not yet completed
          setUploadQueue((prev) =>
            prev.map((item, index) => {
              if (index < data.current - 1) {
                // Files already completed
                return { ...item, status: 'success' as const, progress: 100 };
              } else if (index === data.current - 1) {
                // Currently uploading file
                return { ...item, status: 'uploading' as const, progress: 50 };
              } else {
                // Pending files
                return item;
              }
            })
          );
        }
      });

      const unsubscribeComplete = documentUploadSignalR.onUploadComplete((data) => {
        if (data.uploadSessionId === uploadSessionId) {
          // Mark all successful uploads as complete
          setUploadQueue((prev) =>
            prev.map((item, index) => {
              if (index < data.successfulUploads) {
                return { ...item, status: 'success' as const, progress: 100 };
              } else {
                // Failed uploads
                return {
                  ...item,
                  status: 'error' as const,
                  error: t('Erro ao enviar documento'),
                };
              }
            })
          );

          // Show completion toast
          if (data.status === 'success') {
            toast.success(t('Todos os documentos foram enviados com sucesso!'));
          } else if (data.status === 'partial') {
            toast.warning(
              t('Alguns documentos falharam. {{success}} de {{total}} enviados.', {
                success: data.successfulUploads,
                total: data.totalFiles,
              })
            );
          } else {
            toast.error(t('Falha ao enviar os documentos'));
          }
        }
      });

      const unsubscribeError = documentUploadSignalR.onUploadError((data) => {
        if (data.uploadSessionId === uploadSessionId) {
          toast.error(`${t('Erro no upload')}: ${data.error}`);
        }
      });

      // Prepare files for batch upload
      const batchFiles = filesToUpload.map((pf) => {
        // Create new File with edited filename
        const blob = new Blob([pf.file], { type: pf.file.type });
        const fileToUpload = new File([blob], pf.customFileName || pf.file.name, {
          type: pf.file.type,
          lastModified: pf.file.lastModified,
        });

        return {
          file: fileToUpload,
          documentType: pf.documentType,
          startDate: pf.startDate,
          expirationDate: pf.expirationDate,
          notes: undefined,
        };
      });

      // Call batch upload API
      const result = await documentsService.batchUploadDocuments({
        uploadSessionId,
        ownerType,
        ownerId,
        companyId: ownerType === DocumentOwnerType.Partner ? companyId : undefined,
        files: batchFiles,
      });

      // Process result and update queue with final states
      setUploadQueue((prev) =>
        prev.map((item, index) => {
          const uploadedFile = result.uploadedFiles[index];
          const failure = result.failures.find((f) => f.fileName === item.customFileName);

          if (uploadedFile) {
            return { ...item, status: 'success' as const, progress: 100 };
          } else if (failure) {
            return {
              ...item,
              status: 'error' as const,
              error: failure.error,
            };
          } else {
            return item;
          }
        })
      );

      // Unsubscribe from events
      unsubscribeProgress();
      unsubscribeComplete();
      unsubscribeError();

      // Leave upload session
      await documentUploadSignalR.leaveUploadSession(uploadSessionId);

      // Clear queue after a delay
      setTimeout(() => {
        setUploadQueue([]);
        setIsUploading(false);
        onUploadComplete();
      }, 2000);
    } catch (error: any) {
      console.error('Batch upload error:', error);
      toast.error(
        error.response?.data?.message || t('Erro ao enviar documentos')
      );

      // Mark all files as error
      setUploadQueue((prev) =>
        prev.map((item) => ({
          ...item,
          status: 'error' as const,
          error: error.message || t('Erro ao enviar documento'),
        }))
      );

      setIsUploading(false);
    }
  };

  const getDocumentTypeOptions = () => {
    // Start with empty option
    const emptyOption = { value: -1, label: t('Selecione o tipo de documento...') };

    let options: { value: number; label: string }[] = [];

    if (ownerType === DocumentOwnerType.Company) {
      options = Object.values(CompanyDocumentType)
        .filter((v) => typeof v === 'number')
        .map((type) => ({
          value: type,
          label: getDocumentTypeLabel(type as CompanyDocumentType, ownerType),
        }));
    } else if (ownerType === DocumentOwnerType.Partner) {
      options = Object.values(PartnerDocumentType)
        .filter((v) => typeof v === 'number')
        .map((type) => ({
          value: type,
          label: getDocumentTypeLabel(type as PartnerDocumentType, ownerType),
        }));
    } else {
      options = Object.values(IndividualDocumentType)
        .filter((v) => typeof v === 'number')
        .map((type) => ({
          value: type,
          label: getDocumentTypeLabel(type as IndividualDocumentType, ownerType),
        }));
    }

    return [emptyOption, ...options];
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .file-classification-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .file-classification-row > div {
            width: 100% !important;
            min-width: unset !important;
          }
          .file-classification-delete {
            align-self: flex-end !important;
          }
        }
      `}</style>
      <Card className="border">
        <CardBody>
          <Row>
          <Col md={12}>
            <div
              className={`border border-2 border-dashed rounded p-4 text-center ${
                isDragging ? 'border-primary bg-light' : 'border-secondary'
              } ${isUploading ? 'opacity-50' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{ cursor: isUploading ? 'not-allowed' : 'pointer', minHeight: '150px' }}
              onClick={handleBrowseClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptedFileTypes}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                disabled={isUploading}
              />

              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <i
                  className={`ri-upload-cloud-2-line display-4 ${
                    isDragging ? 'text-primary' : 'text-muted'
                  }`}
                ></i>
                <h5 className="mt-3">
                  {isDragging
                    ? t('Solte os arquivos aqui')
                    : t('Arraste arquivos ou clique para selecionar')}
                </h5>
                <p className="text-muted mb-0">
                  {t('Tipos aceitos: PDF, Word, Excel, Imagens, ZIP, Certificados')}
                </p>
                <p className="text-muted">
                  {t('Tamanho máximo: 5MB por arquivo')}
                </p>
              </div>
            </div>
          </Col>

          {/* Pending Files - Awaiting Classification */}
          {pendingFiles.length > 0 && (
            <Col md={12} className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  {t('Arquivos Pendentes')} ({pendingFiles.length})
                </h6>
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleUploadAll}
                  disabled={isUploading}
                >
                  <i className="ri-upload-2-line me-1"></i>
                  {t('Enviar Todos')}
                </Button>
              </div>
              <div className="alert alert-info mb-3">
                <i className="ri-information-line me-2"></i>
                {t('Classifique cada documento antes de enviar')}
              </div>
              <div>
                {pendingFiles.map((pendingFile, index) => {
                  const hasError = validationErrors.has(pendingFile.id);
                  return (
                    <div
                      key={pendingFile.id}
                      className="file-classification-row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '12px 16px',
                        marginBottom: '8px',
                        border: hasError ? '1px solid #dc3545' : '1px solid #e0e0e0',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        backgroundColor: hasError ? '#fff5f5' : '#fff',
                      }}
                      onMouseEnter={(e) => {
                        if (!hasError) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = hasError ? '#fff5f5' : '#fff';
                      }}
                    >
                    {/* Index number */}
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#666',
                        minWidth: '24px',
                        textAlign: 'center',
                      }}
                    >
                      {index + 1}
                    </div>

                    {/* File name and size */}
                    <div style={{ flex: '1', minWidth: '0' }}>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: '400',
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {pendingFile.file.name}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#999',
                          marginTop: '2px',
                        }}
                      >
                        {(pendingFile.file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>

                    {/* Document type and metadata column */}
                    <div style={{ minWidth: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Document type dropdown */}
                      <Input
                        type="select"
                        id={`docType-${pendingFile.id}`}
                        value={pendingFile.documentType}
                        onChange={(e) =>
                          handleDocumentTypeChange(pendingFile.id, Number(e.target.value))
                        }
                        style={{
                          fontSize: '14px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: pendingFile.documentType === getDefaultDocumentType() ? '#999' : '#333',
                        }}
                      >
                        {getDocumentTypeOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>

                      {/* Show metadata for company documents */}
                      {ownerType === DocumentOwnerType.Company &&
                       pendingFile.documentType !== getDefaultDocumentType() && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {(() => {
                            const metadata = CompanyDocumentMetadata.getMetadata(pendingFile.documentType as CompanyDocumentType);
                            if (!metadata) return null;

                            const sphereLabel = getDocumentSphereLabel(metadata.sphere);
                            const sphereColor = getDocumentSphereColor(metadata.sphere);
                            const validityLabel = formatValidityDays(metadata.validityDays);

                            return (
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <span className={`badge bg-${sphereColor}`} style={{ fontSize: '11px' }}>
                                  {sphereLabel}
                                </span>
                                <span className="badge bg-secondary" style={{ fontSize: '11px' }}>
                                  {validityLabel}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Show date pickers for documents with expiration */}
                      {ownerType === DocumentOwnerType.Company &&
                       pendingFile.documentType !== getDefaultDocumentType() &&
                       CompanyDocumentMetadata.hasExpiration(pendingFile.documentType as CompanyDocumentType) && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <div style={{ flex: 1 }}>
                            <Label for={`startDate-${pendingFile.id}`} style={{ fontSize: '11px', marginBottom: '2px' }}>
                              Data Inicio
                            </Label>
                            <Input
                              type="date"
                              id={`startDate-${pendingFile.id}`}
                              value={pendingFile.startDate || ''}
                              onChange={(e) => handleStartDateChange(pendingFile.id, e.target.value)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Label for={`expirationDate-${pendingFile.id}`} style={{ fontSize: '11px', marginBottom: '2px' }}>
                              Data Expiracao
                            </Label>
                            <Input
                              type="date"
                              id={`expirationDate-${pendingFile.id}`}
                              value={pendingFile.expirationDate || ''}
                              onChange={(e) => handleExpirationDateChange(pendingFile.id, e.target.value)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Validation message */}
                      {pendingFile.documentType === getDefaultDocumentType() && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: hasError ? '#dc3545' : '#ff9800',
                            marginTop: '4px',
                            fontWeight: hasError ? '500' : 'normal',
                          }}
                        >
                          {hasError ? (
                            <>
                              <i className="ri-error-warning-line me-1"></i>
                              {t('Por favor, classifique este documento')}
                            </>
                          ) : (
                            t('Por favor, classifique este documento')
                          )}
                        </div>
                      )}
                    </div>

                    {/* Delete button */}
                    <button
                      type="button"
                      className="file-classification-delete"
                      onClick={() => handleRemovePendingFile(pendingFile.id)}
                      disabled={isUploading}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        color: '#dc3545',
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        opacity: isUploading ? 0.5 : 1,
                        minWidth: '40px',
                      }}
                      onMouseEnter={(e) => {
                        if (!isUploading) {
                          e.currentTarget.style.backgroundColor = '#dc3545';
                          e.currentTarget.style.color = '#fff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isUploading) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#dc3545';
                        }
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                  );
                })}
              </div>
            </Col>
          )}

          {/* Upload Queue - Files Being Uploaded */}
          {uploadQueue.length > 0 && (
            <Col md={12} className="mt-3">
              <h6 className="mb-3">{t('Uploads em Andamento')}</h6>
              {uploadQueue.map((upload, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center flex-grow-1">
                      <i className="ri-file-line me-2"></i>
                      <span className="text-truncate" style={{ maxWidth: '300px' }}>
                        {upload.customFileName || upload.file.name}
                      </span>
                      <span className="badge bg-secondary ms-2">
                        {getDocumentTypeLabel(upload.documentType, ownerType)}
                      </span>
                      {upload.status === 'uploading' && (
                        <span className="badge bg-primary ms-2">
                          {t('Enviando...')}
                        </span>
                      )}
                      {upload.status === 'success' && (
                        <span className="badge bg-success ms-2">
                          <i className="ri-check-line me-1"></i>
                          {t('Enviado')}
                        </span>
                      )}
                      {upload.status === 'error' && (
                        <span className="badge bg-danger ms-2">
                          <i className="ri-close-line me-1"></i>
                          {t('Erro')}
                        </span>
                      )}
                    </div>
                    <span className="text-muted">{upload.progress}%</span>
                  </div>
                  <Progress
                    value={upload.progress}
                    color={
                      upload.status === 'success'
                        ? 'success'
                        : upload.status === 'error'
                        ? 'danger'
                        : 'primary'
                    }
                    style={{ height: '6px' }}
                  />
                  {upload.error && (
                    <small className="text-danger">{upload.error}</small>
                  )}
                </div>
              ))}
            </Col>
          )}
        </Row>
      </CardBody>
    </Card>
    </>
  );
});

DocumentUploadZone.displayName = 'DocumentUploadZone';

export default DocumentUploadZone;
